/**
 * AppHeader — Unified tab-style navigation header for Compliance Hub.
 * Rendered on both the Chat view (MainApp) and Portal view (PortalLayout).
 * Handles its own health + spend polling; reads user info from localStorage.
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleAuthFailure, useLanding } from './AuthGate';
import { authFetch } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface AppHeaderProps {
  /** Inbox/queue badge count for the Portal tab */
  inboxCount?: number;
  /** Whether the current user is an admin (shows badge) */
  isAdminUser?: boolean;
  /** Optional callback for the mobile overflow (⋮) button */
  onOpenOverflow?: () => void;
  /** Open command palette */
  onOpenCommandPalette?: () => void;
  /** Open feedback modal */
  onOpenFeedback?: () => void;
}

export default function AppHeader({
  inboxCount = 0,
  isAdminUser = false,
  onOpenOverflow,
  onOpenCommandPalette,
  onOpenFeedback,
}: AppHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [healthStatus, setHealthStatus] = useState<'ok' | 'warn' | 'error'>('ok');
  const [spendLabel, setSpendLabel] = useState<string>('…');
  // Generation (Anthropic) is probed ONCE on open — not on the 60s poll — so the
  // pill can reflect answer generation without a paid call every minute.
  const [genStatus, setGenStatus] = useState<'checking' | 'ok' | 'error'>('checking');
  const [genCheckedAt, setGenCheckedAt] = useState<string>('');
  const [genError, setGenError] = useState<string>('');

  // Read user info from localStorage (stable once authed)
  const userName = localStorage.getItem('standards-user-name') || '';
  const userRole = localStorage.getItem('standards-user-role') || '';

  const isPortal = location.pathname.startsWith('/portal');
  const { returnToLanding } = useLanding();

  // Poll /api/health every 60 s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/health`);
        if (!cancelled) setHealthStatus(res.ok ? 'ok' : 'warn');
      } catch {
        if (!cancelled) setHealthStatus('error');
      }
    };
    void poll();
    const id = setInterval(() => void poll(), 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Probe answer generation ONCE on open (deep=1 fires a real 1-token Anthropic
  // call). Reload re-checks. Kept off the 60s poll so it costs ~nothing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // authFetch, not fetch: deep=1 is now gated to authenticated callers so a public
        // loop of the endpoint can't burn Anthropic credit (Codex review 2026-08-04).
        const res = await authFetch(`${API_BASE}/api/health?deep=1`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({})) as { generation?: string; generation_error?: string };
        if (cancelled) return;
        if (data.generation === 'ok') setGenStatus('ok');
        else if (data.generation === 'error') { setGenStatus('error'); setGenError(data.generation_error ?? ''); }
        else setGenStatus('ok'); // probe didn't report — don't false-alarm
        setGenCheckedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      } catch {
        if (!cancelled) {
          setGenStatus('error');
          setGenCheckedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll /api/user/spend every 5 min
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/user/spend`);
        if (res.ok) {
          const data = await res.json() as {
            today?: number;
            this_week?: number;
            this_month?: number;
            total_cost_usd?: number;
            spend_30d?: number;
            cost_usd?: number;
          };
          // Use today's spend; fall back to 30d fields for backward compat
          const today = data.today ?? null;
          const monthly = data.this_month ?? data.total_cost_usd ?? data.spend_30d ?? data.cost_usd ?? null;
          if (!cancelled) {
            if (today !== null) {
              setSpendLabel(`$${Number(today).toFixed(2)} today`);
            } else if (monthly !== null) {
              setSpendLabel(`$${Number(monthly).toFixed(2)} / 30d`);
            }
          }
        }
      } catch { /* ignore */ }
    };
    void poll();
    const id = setInterval(() => void poll(), 5 * 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <header className="header header--unified">
      {/* Left: wordmark (⬡ return-to-landing button removed — too easy to accidentally trigger) */}
      <div className="header-left">
        <span
          className="header-wordmark"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
          title="Compliance Hub — go to Chat"
        >
          ⬡ Compliance Hub{' '}
          <span
            style={{
              fontSize: '0.55em',
              fontWeight: 500,
              color: 'var(--accent)',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              verticalAlign: 'middle',
            }}
          >
            Beta
          </span>
        </span>
      </div>

      {/* Right: cmd palette trigger, feedback, status, user info, sign out, overflow */}
      <div className="header-right">
        {/* API status pill — reachability (60s poll) + answer generation (once on open) */}
        {(() => {
          // reachability drives 'error'; a failed generation probe downgrades to 'warn'
          const pillState: 'ok' | 'warn' | 'error' =
            healthStatus === 'error' ? 'error'
              : genStatus === 'error' ? 'warn'
                : healthStatus === 'warn' ? 'warn'
                  : 'ok';
          const reachLine =
            healthStatus === 'ok' ? 'Corpus + API: reachable (re-checked every 60s).'
              : healthStatus === 'warn' ? 'Corpus + API: degraded (re-checked every 60s).'
                : 'Corpus + API: unreachable (re-checked every 60s).';
          const genLine =
            genStatus === 'checking' ? 'Answer generation: checking…'
              : genStatus === 'ok' ? `Answer generation: OK — checked once when you opened the app${genCheckedAt ? ` (${genCheckedAt})` : ''}.`
                : `Answer generation: FAILED when you opened the app${genCheckedAt ? ` (${genCheckedAt})` : ''}${genError ? ` — ${genError}` : ''}. Queries auto-retry on the fallback model; reload to re-check.`;
          return (
            <span
              className={`header-api-status header-api-status--${pillState}`}
              title={`${reachLine}\n${genLine}`}
            >
              <span className="header-status-dot" />
              <span className="header-api-status-label">
                {pillState === 'ok' ? 'API OK' : pillState === 'warn' ? 'API degraded' : 'API down'}
              </span>
            </span>
          );
        })()}
        {/* Spend badge — always visible once loaded */}
        <span className="header-spend-badge" title="Your API spend today">
          {spendLabel}
        </span>
        {userName && (
          <span className="auth-user-info">
            <span className="auth-user-name">{userName}</span>
            {userRole === 'admin' && (
              <span className="auth-user-badge">Admin</span>
            )}
          </span>
        )}
        {onOpenCommandPalette && (
          <button
            className="header-cmd-btn"
            onClick={onOpenCommandPalette}
            title="Search"
            aria-label="Open command palette"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </button>
        )}
        <a
          href="/user-guide.html"
          target="_blank"
          rel="noopener noreferrer"
          title="User Guide (opens in new tab)"
          aria-label="Open User Guide"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          📖 Guide
        </a>
        {onOpenFeedback && (
          <button
            className="header-feedback-btn"
            onClick={onOpenFeedback}
            title="Submit feedback"
            aria-label="Submit feedback"
          >
            Feedback
          </button>
        )}
        <button className="auth-signout-btn" onClick={handleAuthFailure}>
          Sign out
        </button>
        {onOpenOverflow && (
          <button
            className="header-overflow-btn"
            onClick={onOpenOverflow}
            aria-label="More options"
          >
            ⋮
          </button>
        )}
      </div>
    </header>
  );
}
